import { JSX, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, MinusIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import useAppStore from '../stores/useAppStore';
import { siteContent } from '../data/content';
import ChatStatusIndicator from '../components/chat/ChatStatusIndicator';
import { uiLogger } from '../utils/logger';
import { chatApi } from '../services/api/chatApi';
import { handleApiError } from '../services/api/errorHandler';
import '../styles/chat.css';

export default function ChatPage(): JSX.Element {
  const navigate = useNavigate();
  const { 
    chatMessages, 
    addMessage: addChatMessage,
    isTyping,
    setIsTyping,
    minimizeChat,
    closeChat,
    unreadCount,
    markMessagesAsRead,
    isChatOpen,
    openChat,
    chatSessionId,
    setChatSessionId
  } = useAppStore();
  
  const [inputMessage, setInputMessage] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  const { chat } = siteContent;

  // Add chat-page class to body when component mounts
  useEffect(() => {
    document.body.classList.add('chat-page');
    
    // Remove the class when component unmounts
    return () => {
      document.body.classList.remove('chat-page');
    };
  }, []);

  // Scroll to bottom when messages change or bot is typing
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isTyping]);

  // Focus input when page loads
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
    
    // Mark messages as read when chat page is opened
    if (unreadCount > 0) {
      markMessagesAsRead();
    }
    
    // Ensure chat is marked as open
    if (!isChatOpen) {
      openChat();
    }
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async (): Promise<void> => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setError(null);

    // Add user message
    addChatMessage({
      role: 'user',
      content: [{ text: userMessage }]
    });

    // Show typing indicator
    setIsTyping(true);

    try {
      // Make sure chat is marked as open when sending messages
      if (!isChatOpen) {
        openChat();
      }
      
      const response = await chatApi.sendMessage(userMessage, chatSessionId);
      
      // Update session ID if new
      if (response.sessionId && response.sessionId !== chatSessionId) {
        setChatSessionId(response.sessionId);
      }

      // Add assistant response
      addChatMessage({
        role: 'assistant',
        content: response.message.content
      });
    } catch (err) {
      setError(handleApiError(err));
      addChatMessage({
        role: 'assistant',
        content: [{ text: 'Sorry, I encountered an error. Please try again.' }],
        isError: true
      });
    } finally {
      setIsTyping(false);
      
      // Ensure chat remains open after sending message
      if (!isChatOpen) {
        openChat();
      }
    }
  };
  
  const handleMinimize = (): void => {
    minimizeChat();
    
    // Try to navigate to the previous path if available
    try {
      const previousPath = sessionStorage.getItem('chatPreviousPath');
      if (previousPath) {
        navigate(previousPath);
      } else {
        // Fallback to going back in history
        navigate(-1);
      }
    } catch (error) {
      // If there's an error, just go back in history
      navigate(-1);
    }
  };
  
  const handleClose = (): void => {
    // Clear chat messages when closing
    closeChat();
    
    // Try to navigate to the previous path if available
    try {
      const previousPath = sessionStorage.getItem('chatPreviousPath');
      if (previousPath) {
        navigate(previousPath);
      } else {
        // Fallback to going back in history
        navigate(-1);
      }
    } catch (error) {
      // If there's an error, just go back in history
      navigate(-1);
    }
  };
  
  return (
    <div className="chat-container bg-gray-50 dark:bg-gray-900">
      {/* Header - Sticky on mobile, normal on desktop */}
      <div className="chat-header">
        <div className="container-max section-padding">
          <div className="flex items-center justify-between py-4">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {chat.title}
            </h1>
            <div className="flex space-x-2">
              <button
                onClick={handleMinimize}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={chat.minimizeButton}
              >
                <MinusIcon className="h-5 w-5" />
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label={chat.closeButton}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area - Scrollable content */}
      <div className="messages-area">
        {chatMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full min-h-[200px]">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <p className="mb-2">{chat.welcome}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4 px-4">
            {chatMessages.map((message) => (
              <div
                key={message.id}
                className={`${message.role === 'user' ? 'text-right' : ''}`}
              >
                <div className={`inline-block rounded-lg px-4 py-2 max-w-[80%] ${
                  message.role === 'user'
                    ? 'bg-primary-600 text-white'
                    : message.isError
                    ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}>
                  {message.content.map((content, index) => (
                    <p key={index} className="whitespace-pre-wrap">
                      {content.text}
                    </p>
                  ))}
                  <p className="text-xs opacity-70 mt-1">
                    {new Date(message.timestamp).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="text-left">
                <div className="inline-block bg-gray-100 dark:bg-gray-700 p-3 rounded-lg">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
      
      {/* Error message if any */}
      {error && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-100 dark:border-red-900/30">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
      
      {/* Input Area - Sticky on mobile, normal on desktop */}
      <div className="input-area">
        <div className="flex space-x-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={chat.placeholder}
            className="flex-1 resize-none border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={1}
            style={{ minHeight: '2.5rem', maxHeight: '6rem' }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white p-2 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
            aria-label={chat.sendButton}
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
